import { forwardRef, useState } from 'react'

import { tv, VariantProps } from 'tailwind-variants'

import { Icon } from '@/components/atoms/Icon'
import { Label } from '@/components/atoms/Label'

export const fileUploadVariants = tv({
  slots: {
    containerSlot:
      'border-surface-white-400 relative flex items-center justify-center gap-2 rounded-lg border-[0.5px] border-dashed text-center'
  },
  variants: {
    hasFiles: {
      true: {
        containerSlot: 'p-4'
      },
      false: {
        containerSlot: 'py-4 pt-4'
      }
    }
  }
})

export type TFileUploadProps = React.InputHTMLAttributes<HTMLInputElement> &
  VariantProps<typeof fileUploadVariants> & {
    label?: string
    accept?: string[]
    maxFileSize?: number
  }

export const FileUpload = forwardRef<HTMLInputElement, TFileUploadProps>(
  ({ accept, id, label, maxFileSize, onChange, ...props }, ref) => {
    const [fileNames, setFileNames] = useState<string[]>([])
    const { containerSlot } = fileUploadVariants()

    const defaultAcceptedFileTypes = ['.pdf', '.png', '.jpg']
    const defaultMaxFileSize = 10 * 1024 * 1024 // 10MB

    const acceptedFileTypes = (accept || defaultAcceptedFileTypes).map(
      fileType => `.${fileType.replaceAll('.', '').toLowerCase()}`
    )

    const acceptedFileTypesLabel = acceptedFileTypes
      .map(fileType => fileType.replace('.', '').toUpperCase())
      .join(', ')

    const maxFileSizeLabel = `${Math.floor(
      (maxFileSize || defaultMaxFileSize) / 1024 / 1024
    )}MB`

    const handleOnFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files

      if (files) {
        const fileNames = Array.from(files).map(file => file.name)
        setFileNames(fileNames)
      }

      onChange?.(e)
    }

    return (
      <div className={containerSlot({ hasFiles: fileNames.length > 0 })}>
        <input
          className='absolute inset-0 h-full w-full cursor-pointer opacity-0'
          type='file'
          id={id}
          ref={ref}
          accept={acceptedFileTypes.join(',')}
          onChange={handleOnFileChange}
          {...props}
        />
        {fileNames.length === 0 ? (
          <div className='flex flex-col gap-2'>
            <Icon name='upload' size='xl' color='surface-black-50' />
            <Label
              htmlFor={id}
              size='body-md'
              weight='semibold'
              color='text-surface-black-50'
            >
              {label ||
                `Drag and drop or browse (${acceptedFileTypesLabel} - max ${maxFileSizeLabel})`}
            </Label>
          </div>
        ) : (
          <div className='flex flex-col gap-2'>
            {fileNames.map(fileName => (
              <p key={fileName}>{fileName}</p>
            ))}
          </div>
        )}
      </div>
    )
  }
)

FileUpload.displayName = 'FileUpload'
